import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import { toast } from "react-toastify";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { extractErrorMessage } from "../../utils/errorMessage";
import { DataTable, PageHeader, StatusChip } from "../ui";
import { LeaveAllocationBreakdown, ManagerMonthlyLeaveTable, leaveTypeLabel, AutoAbsentChip, isAutoAbsentLeave } from "./leaveAllocationUi";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function LeaveDecisionModal({ open, action, row, onClose, onConfirm, submitting }) {
  const [rejectionReason, setRejectionReason] = useState("");
  const isApprove = action === "approved";
  const accent = isApprove ? "#22C55E" : "#EF4444";
  const emp = row?.employee;

  useEffect(() => {
    if (open) setRejectionReason("");
  }, [open, row?._id, action]);

  if (!row) return null;

  const handleConfirm = () => {
    if (!isApprove && !rejectionReason.trim()) {
      toast.error("Please enter a reason for rejection");
      return;
    }
    onConfirm(rejectionReason.trim());
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 460,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Box sx={{ height: 4, bgcolor: accent }} />

      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: isApprove ? "#EDFAF2" : "#FEE2E2",
                color: accent,
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              {(emp?.name || "?").slice(0, 1).toUpperCase()}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
                {isApprove ? "Approve leave" : "Reject leave"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Review request details below
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={submitting} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, pb: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <DetailRow icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />} label="Employee">
            {emp?.name || "—"}
            {emp?.employeeid && (
              <Typography component="span" sx={{ fontSize: 11, color: "text.secondary", ml: 0.75 }}>
                ({emp.employeeid})
              </Typography>
            )}
          </DetailRow>
          <DetailRow icon={<BeachAccessIcon sx={{ fontSize: 16 }} />} label="Applied as">
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={leaveTypeLabel(row.leaveType)} sx={{ height: 22, fontWeight: 600 }} />
              {isAutoAbsentLeave(row) ? <AutoAbsentChip /> : null}
            </Stack>
          </DetailRow>
          <DetailRow icon={<EventOutlinedIcon sx={{ fontSize: 16 }} />} label="Dates">
            {fmtDate(row.startDate)} — {fmtDate(row.endDate)}
            <Typography component="span" sx={{ display: "block", fontSize: 11, color: "text.secondary", mt: 0.25 }}>
              {row.totalDays ?? 1} working day{row.totalDays === 1 ? "" : "s"}
              {row.halfDay ? ` · half day` : ""}
              {row.excludedDays > 0 ? ` · ${row.excludedDays} off day(s) excluded` : ""}
            </Typography>
          </DetailRow>
          <DetailRow icon={<EventOutlinedIcon sx={{ fontSize: 16 }} />} label="Deducted from">
            <LeaveAllocationBreakdown row={row} />
          </DetailRow>
          <DetailRow icon={<NotesOutlinedIcon sx={{ fontSize: 16 }} />} label="Reason" last>
            <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>
              {row.reason?.trim() || "—"}
            </Typography>
          </DetailRow>
        </Box>

        {isApprove ? (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5, textAlign: "center" }}>
            Confirm approval? Leave / LOP was already updated when this request was applied.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5, textAlign: "center" }}>
              Rejecting will restore paid leave or reverse LOP days on the employee&apos;s record.
            </Typography>
            <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={3}
            label="Reason for rejection"
            placeholder="Required — explain why this request is rejected"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={submitting}
            sx={{ mt: 1.5 }}
            required
          />
          </>
        )}
      </Box>

      <Divider />

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 1.5 }}>
        <Button
          size="small"
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={submitting}
          onClick={handleConfirm}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : isApprove ? (
              <CheckCircleOutlineIcon />
            ) : (
              <CancelOutlinedIcon />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            minWidth: 110,
            bgcolor: accent,
            "&:hover": { bgcolor: isApprove ? "#16A34A" : "#DC2626" },
          }}
        >
          {submitting ? "Processing…" : isApprove ? "Approve" : "Reject"}
        </Button>
      </Stack>
    </Dialog>
  );
}

function DetailRow({ icon, label, children, last }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{ py: 0.75, borderBottom: last ? "none" : "1px dashed", borderColor: "divider" }}
    >
      <Box sx={{ color: "text.secondary", mt: 0.15 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
          {label}
        </Typography>
        <Box sx={{ fontSize: 13, fontWeight: 600, mt: 0.15 }}>{children}</Box>
      </Box>
    </Stack>
  );
}

export default function ManagerLeaveRequestsNew() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [employeeMonthlyLeave, setEmployeeMonthlyLeave] = useState({});
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([
    "sick", "casual", "earned", "other", "compOff", "compensatory off",
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoints.leave.managerRequests);
      const data = res.data?.leaveRequests || res.data?.data || res.data || [];
      setRows(Array.isArray(data) ? data : []);
      setEmployeeMonthlyLeave(res.data?.employeeMonthlyLeave || {});
    } catch (err) {
      if (err.response?.status !== 404) toast.error(extractErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // SR 224: load leave types dynamically so new types appear in the filter
    api.get(endpoints.leave.types).then((res) => {
      const types = res.data?.data || res.data?.leaveTypes || res.data || [];
      if (Array.isArray(types) && types.length > 0) {
        setLeaveTypeOptions(types.map((t) => t.name || t.code || t));
      }
    }).catch(() => {}); // fallback to defaults on error

    const reloadQuietly = () => {
      api.get(endpoints.leave.managerRequests).then((res) => {
        const data = res.data?.leaveRequests || res.data?.data || res.data || [];
        setRows(Array.isArray(data) ? data : []);
        setEmployeeMonthlyLeave(res.data?.employeeMonthlyLeave || {});
      }).catch(() => {});
    };
    const onLeaveChanged = (e) => {
      if (e?.detail?.kind === "leave.withdrawn" && e.detail?.metadata?.requestId) {
        setRows((prev) => prev.filter((r) => String(r._id) !== String(e.detail.metadata.requestId)));
      }
      reloadQuietly();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") reloadQuietly();
    };
    window.addEventListener("hrms:leave-changed", onLeaveChanged);
    window.addEventListener("focus", reloadQuietly);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("hrms:leave-changed", onLeaveChanged);
      window.removeEventListener("focus", reloadQuietly);
      document.removeEventListener("visibilitychange", onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeDialog = () => {
    if (!submitting) setDialog(null);
  };

  const handleConfirm = async (rejectionReason) => {
    if (!dialog?.row) return;
    setSubmitting(true);
    try {
      await api.put(endpoints.leave.updateStatus(dialog.row._id), {
        status: dialog.action,
        rejectionReason: dialog.action === "rejected" ? rejectionReason : undefined,
      });
      toast.success(
        dialog.action === "approved"
          ? "Leave approved successfully"
          : "Leave rejected"
      );
      setDialog(null);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      field: "employee.name",
      label: "Employee",
      render: (r) => (
        <Box>
          <Typography fontWeight={500}>{r.employee?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {r.employee?.employeeid}
          </Typography>
        </Box>
      ),
    },
    { field: "leaveType", label: "Applied as", render: (r) => (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip size="small" label={leaveTypeLabel(r.leaveType)} variant="outlined" />
        {isAutoAbsentLeave(r) ? <AutoAbsentChip /> : null}
      </Stack>
    ) },
    { field: "startDate", label: "From", render: (r) => fmtDate(r.startDate) },
    { field: "endDate", label: "To", render: (r) => fmtDate(r.endDate) },
    {
      field: "totalDays",
      label: "Days",
      align: "right",
      render: (r) => (
        <Typography fontWeight={500}>
          {r.totalDays || 1}
          {r.halfDay ? ` (½${r.halfDaySlot ? ` ${r.halfDaySlot}` : ""})` : ""}
        </Typography>
      ),
    },
    {
      field: "paidDays",
      label: "Deducted from",
      wrap: true,
      render: (r) => <LeaveAllocationBreakdown row={r} compact />,
    },
    { field: "reason", label: "Reason", wrap: true },
    { field: "status", label: "Status", render: (r) => <StatusChip status={r.status} /> },
    {
      label: "Actions",
      sortable: false,
      exportable: false,
      render: (r) =>
        r.status === "pending" ? (
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              onClick={(e) => {
                e.stopPropagation();
                setDialog({ row: r, action: "approved" });
              }}
            >
              Approve
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              onClick={(e) => {
                e.stopPropagation();
                setDialog({ row: r, action: "rejected" });
              }}
            >
              Reject
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <Box p={3}>
      <PageHeader
        title="Leave Approvals"
        subtitle="1.5 leave days accrue each month. Unused days add up; extra days become LOP for that month."
        icon={<BeachAccessIcon />}
      />

      <ManagerMonthlyLeaveTable snapshots={employeeMonthlyLeave} />

      <DataTable
        rows={rows}
        columns={columns}
        loading={loading}
        filters={[
          {
            key: "status",
            label: "Status",
            field: "status",
            options: ["pending", "approved", "rejected", "cancelled"].map((s) => ({
              value: s,
              label: s,
            })),
          },
          {
            key: "type",
            label: "Type",
            field: "leaveType",
            options: leaveTypeOptions.map((s) => ({
              value: s,
              label: s === "compOff" ? "Compensatory Off" : s,
            })),
          },
        ]}
        emptyTitle="No leave requests"
        exportFilename="team-leaves.csv"
      />

      <LeaveDecisionModal
        open={!!dialog}
        action={dialog?.action}
        row={dialog?.row}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        submitting={submitting}
      />
    </Box>
  );
}
